import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Copy, Crosshair, ExternalLink, FileSearch, RefreshCw, ShieldCheck, Sparkles, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, ErrorState, Skeleton, Tab, TabList, TabPanel, Tabs, describeError } from '@/components/primitives';
import { apiGet, copyText } from './api';
import { ago, hostname, isoDate } from './format';
import '@/styles/vuln.css';

/*
 * The vulnerability brief for one CVE. Facts (NVD, CISA KEV, FIRST EPSS, OSV, MITRE CWE and ATT&CK,
 * Exploit-DB and Nuclei indexes) load first; the AI-written explanation, remediation and hunting
 * queries follow, from cache when another organization or an earlier visit already produced them.
 */

export const PRIORITY_TONE = { Act: 'danger', Attend: 'warning', 'Track*': 'info', Track: 'neutral' };

export function PriorityBadge({ decision, size = 'sm', title }) {
  if (!decision) return <span className="dash-muted">-</span>;
  return <Badge tone={PRIORITY_TONE[decision] || 'neutral'} size={size} title={title}>{decision}</Badge>;
}

export const pct = (v, digits = 1) => (v == null ? null : `${(v * 100).toFixed(v * 100 < 1 ? 2 : digits)}%`);

function List({ items, ordered }) {
  if (!items?.length) return null;
  const Tag = ordered ? 'ol' : 'ul';
  return <Tag className="vi-list">{items.map((x, i) => <li key={i}>{x}</li>)}</Tag>;
}

function Section({ title, icon: Icon, children, aside }) {
  return (
    <section className="vi-section">
      <div className="vi-section-head">
        <h4 className="vi-h">{Icon && <Icon size={14} aria-hidden="true" />}{title}</h4>
        {aside}
      </div>
      {children}
    </section>
  );
}

function AiPending() {
  return (
    <div className="vi-pending" role="status">
      <Skeleton style={{ height: 12, width: '92%' }} />
      <Skeleton style={{ height: 12, width: '80%' }} />
      <Skeleton style={{ height: 12, width: '64%' }} />
      <span className="t-small">Writing the analysis. The first time a CVE is opened this takes up to a minute.</span>
    </div>
  );
}

function AiUnavailable({ onRetry, busy }) {
  return (
    <p className="t-small vi-note">
      The AI analysis is not available right now (the language model could not be reached). Facts from public sources are shown.{' '}
      <button type="button" className="is-btn is-btn--ghost is-btn--sm" onClick={onRetry} disabled={busy}>Try again</button>
    </p>
  );
}

function Signals({ f, priority }) {
  const x = f.public_exploit;
  return (
    <dl className="dash-metrics vi-signals">
      <div className="dash-metric"><dt>Priority</dt><dd><PriorityBadge decision={priority.decision} /></dd></div>
      <div className="dash-metric"><dt>CVSS</dt><dd>{f.cvss?.score != null ? `${f.cvss.score} ${f.cvss.severity ? f.cvss.severity.toLowerCase() : ''}` : 'Not scored'}</dd></div>
      <div className="dash-metric" title="FIRST EPSS: probability of exploitation in the next 30 days">
        <dt>EPSS</dt><dd>{f.epss ? `${pct(f.epss.score)} (top ${Math.max(1, Math.round((1 - f.epss.percentile) * 100))}%)` : 'Not scored'}</dd>
      </div>
      <div className="dash-metric"><dt>Exploited in the wild</dt><dd>{f.kev ? 'Yes, CISA KEV' : 'Not confirmed'}</dd></div>
      <div className="dash-metric"><dt>Public exploit</dt><dd>{x ? `Yes${x.metasploit ? ', Metasploit' : ''}` : (f.exploit_index_loaded ? 'None known' : 'Unknown')}</dd></div>
      <div className="dash-metric"><dt>Scanner template</dt><dd>{f.nuclei ? 'Nuclei' : (f.exploit_index_loaded ? 'None' : 'Unknown')}</dd></div>
      {f.published && <div className="dash-metric"><dt>Published</dt><dd>{isoDate(f.published)}</dd></div>}
    </dl>
  );
}

function Overview({ d, ai, aiState, retry }) {
  const f = d.facts;
  return (
    <div className="vi-stack">
      <div className={`dash-callout vi-priority vi-priority--${(PRIORITY_TONE[d.priority.decision] || 'neutral')}`}>
        <div className="dash-callout-title"><span>Priority: {d.priority.decision}</span><span className="t-small">SSVC decision tree</span></div>
        <p className="vi-p">{d.priority.text}</p>
        <ul className="vi-reasons">{d.priority.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
      </div>
      <Signals f={f} priority={d.priority} />
      {f.vector?.phrases?.length > 0 && <p className="t-small vi-note">Attack conditions: {f.vector.phrases.join(', ')}.</p>}

      <Section title="What it is" icon={Sparkles}>
        {aiState === 'loading' ? <AiPending /> : ai?.explanation?.plain ? (
          <>
            <p className="vi-p vi-lead">{ai.explanation.plain}</p>
            {ai.explanation.technical && <p className="vi-p">{ai.explanation.technical}</p>}
          </>
        ) : (
          <>
            <p className="vi-p">{f.summary || 'NVD has not published a description for this CVE yet.'}</p>
            {aiState === 'unavailable' && <AiUnavailable onRetry={retry} />}
          </>
        )}
      </Section>

      {ai?.explanation?.attack_flow?.length > 0 && (
        <Section title="How an attack unfolds">
          <List items={ai.explanation.attack_flow} ordered />
        </Section>
      )}

      {(d.your_context.length > 0 || ai?.insights?.length > 0) && (
        <Section title="Insights">
          {d.your_context.length > 0 && <List items={d.your_context} />}
          {ai?.insights?.length > 0 && <List items={ai.insights} />}
        </Section>
      )}

      {(f.cwes.length > 0 || f.techniques.length > 0) && (
        <Section title="Weakness and attacker techniques">
          <div className="vi-chips">
            {f.cwes.map((w) => (
              <a key={w.id} className="vi-chip" href={w.url} target="_blank" rel="noopener noreferrer" title={w.description}>
                <span className="dash-mono">{w.id}</span> {w.name} <ExternalLink aria-hidden="true" />
              </a>
            ))}
            {f.techniques.map((t) => (
              <a key={t.id} className="vi-chip" href={t.url} target="_blank" rel="noopener noreferrer" title={t.tactics.join(', ')}>
                <span className="dash-mono">{t.id}</span> {t.name} <ExternalLink aria-hidden="true" />
              </a>
            ))}
          </div>
        </Section>
      )}

      {d.threats.length > 0 && (
        <Section title="Cited in your threats">
          <ul className="vi-list">
            {d.threats.slice(0, 5).map((t) => (
              <li key={t.attack_id}><Link to={`/threats?id=${encodeURIComponent(t.attack_id)}`}>{t.name}</Link>{t.actor ? <span className="dash-muted"> · {t.actor}</span> : null}</li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function rangeText(a) {
  const parts = [a.version && `version ${a.version}`, a.start_incl && `from ${a.start_incl}`, a.start_excl && `after ${a.start_excl}`,
    a.end_incl && `up to ${a.end_incl}`, a.end_excl && `before ${a.end_excl}`].filter(Boolean);
  return parts.length ? parts.join(', ') : 'all versions';
}

function Remediation({ d, ai, aiState, retry }) {
  const f = d.facts;
  const r = ai?.remediation;
  const fixes = f.osv?.fixes || [];
  const mitigations = f.cwes.flatMap((w) => w.mitigations.map((m) => ({ ...m, cwe: w.id }))).slice(0, 5);
  return (
    <div className="vi-stack">
      {f.kev && (
        <div className="dash-callout dash-callout--kev">
          <div className="dash-callout-title"><span>CISA required action</span>{f.kev.due_date && <span>Federal deadline {isoDate(f.kev.due_date)}</span>}</div>
          {f.kev.required_action}
        </div>
      )}
      <Section title="Fix" icon={Wrench}>
        {aiState === 'loading' ? <AiPending /> : <p className="vi-p vi-lead">{r?.patch || d.patch_from_facts}</p>}
        {aiState === 'unavailable' && <AiUnavailable onRetry={retry} />}
      </Section>

      {fixes.length > 0 && (
        <Section title="Fixed versions" aside={<span className="t-small">OSV.dev</span>}>
          <div className="dash-table-wrap">
            <table className="dash-table vi-table">
              <thead><tr><th scope="col">Package</th><th scope="col">Ecosystem</th><th scope="col">Fixed in</th></tr></thead>
              <tbody>
                {fixes.map((x) => (
                  <tr key={`${x.ecosystem}:${x.package}`}>
                    <td className="dash-mono">{x.package}</td><td>{x.ecosystem}</td><td className="dash-mono">{x.fixed.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {f.affected.length > 0 && (
        <Section title="Affected products" aside={<span className="t-small">NVD{f.affected_total > f.affected.length ? `, ${f.affected.length} of ${f.affected_total}` : ''}</span>}>
          <ul className="vi-list vi-list--compact">
            {f.affected.slice(0, 8).map((a, i) => <li key={i}><span className="vi-cap">{a.vendor} {a.product}</span> <span className="dash-muted">{rangeText(a)}</span></li>)}
          </ul>
          {f.affected.length > 8 && <p className="t-small vi-note">And {f.affected_total - 8} more product entries on NVD.</p>}
        </Section>
      )}

      {r && (
        <>
          {r.workarounds.length > 0 && <Section title="If you cannot patch yet"><List items={r.workarounds} /></Section>}
          {r.compensating_controls.length > 0 && <Section title="Reduce exposure"><List items={r.compensating_controls} /></Section>}
          {r.verify.length > 0 && <Section title="Confirm the fix" icon={ShieldCheck}><List items={r.verify} /></Section>}
          {r.if_compromised.length > 0 && <Section title="If you suspect exploitation" icon={AlertTriangle}><List items={r.if_compromised} ordered /></Section>}
        </>
      )}

      {mitigations.length > 0 && (
        <Section title="MITRE recommended mitigations" aside={<span className="t-small">CWE</span>}>
          <ul className="vi-list">
            {mitigations.map((m, i) => (
              <li key={i}><span className="dash-muted">{m.cwe}{m.phase ? `, ${m.phase.toLowerCase()}` : ''}: </span>{m.text}</li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function Hunting({ d, ai, aiState, retry }) {
  const f = d.facts;
  const h = ai?.hunting;
  const [platform, setPlatform] = useState(null);
  const queries = h?.queries || [];
  const active = platform && queries.some((q) => q.platform === platform) ? platform : queries[0]?.platform;
  const copy = async (q) => {
    if (await copyText(q.query)) toast.success(`${q.label} query copied`);
    else toast.error("Couldn't copy to the clipboard");
  };
  if (aiState === 'loading') return <AiPending />;
  return (
    <div className="vi-stack">
      {!h ? (aiState === 'unavailable' ? <AiUnavailable onRetry={retry} /> : null) : (
        <>
          <div className={`dash-callout ${h.possible ? 'dash-callout--ai' : ''}`}>
            <div className="dash-callout-title"><span>{h.possible ? 'Huntable' : 'No reliable hunt'}</span></div>
            {h.reason}
          </div>
          {h.hypothesis && <Section title="Hypothesis" icon={Crosshair}><p className="vi-p">{h.hypothesis}</p></Section>}
          {h.data_sources.length > 0 && (
            <Section title="Data you need">
              <div className="vi-chips">{h.data_sources.map((s) => <Badge key={s} size="sm">{s}</Badge>)}</div>
            </Section>
          )}
          {queries.length > 0 && (
            <Section title="Queries" aside={<span className="t-small">AI-drafted. Test in your SIEM before scheduling.</span>}>
              <Tabs value={active} onValueChange={setPlatform}>
                <TabList label="Query platform">
                  {queries.map((q) => <Tab key={q.platform} value={q.platform}>{q.label}</Tab>)}
                </TabList>
                {queries.map((q) => (
                  <TabPanel key={q.platform} value={q.platform}>
                    <p className="vi-p"><strong>{q.title}</strong></p>
                    <div className="dash-query">
                      <pre tabIndex={0} aria-label={`${q.label} query`}>{q.query}</pre>
                      <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => copy(q)}><Copy aria-hidden="true" /> Copy</button>
                    </div>
                    {q.explanation && <p className="vi-p"><span className="dash-muted">A hit means: </span>{q.explanation}</p>}
                    {q.false_positives && <p className="vi-p"><span className="dash-muted">False positives: </span>{q.false_positives}</p>}
                  </TabPanel>
                ))}
              </Tabs>
            </Section>
          )}
          {h.post_exploitation.length > 0 && <Section title="After a successful exploit, look for"><List items={h.post_exploitation} /></Section>}
        </>
      )}
      {f.nuclei && (
        <Section title="Scan your own assets" icon={FileSearch}>
          <p className="vi-p">
            A community Nuclei template checks for this flaw: <a className="dash-link" href={f.nuclei.url} target="_blank" rel="noopener noreferrer">{f.nuclei.name || 'template'} <ExternalLink aria-hidden="true" /></a>.
            Run it only against systems you own or are authorised to test.
          </p>
        </Section>
      )}
    </div>
  );
}

const SOURCE_LINKS = [
  ['NVD', 'https://nvd.nist.gov'], ['CISA KEV', 'https://www.cisa.gov/known-exploited-vulnerabilities-catalog'], ['FIRST EPSS', 'https://www.first.org/epss'],
  ['OSV.dev', 'https://osv.dev'], ['MITRE CWE', 'https://cwe.mitre.org'], ['MITRE ATT&CK®', 'https://attack.mitre.org'],
  ['Exploit-DB index', 'https://www.exploit-db.com'], ['Nuclei templates', 'https://github.com/projectdiscovery/nuclei-templates'],
];

function Sources({ d, ai, busy, regenerate }) {
  const refs = d.references || [];
  return (
    <div className="vi-stack">
      {refs.length > 0 && (
        <Section title="References">
          <ul className="vi-list vi-list--compact">
            {refs.map((r, i) => (
              <li key={`${r.url}-${i}`}>
                <a className="dash-link" href={r.url} target="_blank" rel="noopener noreferrer">{hostname(r.url) || r.url} <ExternalLink aria-hidden="true" /></a>
                {r.tags?.length > 0 && <span className="dash-muted"> {r.tags.join(', ')}</span>}
              </li>
            ))}
          </ul>
        </Section>
      )}
      <Section title="Where this comes from">
        <p className="t-small vi-note">
          {SOURCE_LINKS.map(([name, url], i) => (
            <React.Fragment key={name}>{i > 0 && ', '}<a className="dash-link" href={url} target="_blank" rel="noopener noreferrer">{name}</a></React.Fragment>
          ))}. Exploit-DB is used only to tell whether public exploit code exists; no exploit is linked or stored.
          {ai?.generated_at && ` Analysis written ${ago(ai.generated_at)}${ai.model ? ` by ${ai.model}` : ''}${ai.cached ? ', from cache' : ''}.`}
        </p>
        <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={regenerate} disabled={busy}>
          <RefreshCw className={busy ? 'is-spinner' : undefined} aria-hidden="true" /> {busy ? 'Rewriting…' : 'Rewrite analysis'}
        </button>
      </Section>
    </div>
  );
}

export default function VulnBrief({ cveId, tab: tabProp, onTab, onHuntCount }) {
  const [facts, setFacts] = useState(null);
  const [error, setError] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiState, setAiState] = useState('loading');
  const [tabState, setTabState] = useState('overview');
  const tab = tabProp || tabState;
  const setTab = onTab || setTabState;

  const loadAi = useCallback(async (refresh = false) => {
    setAiState('loading');
    try {
      const res = await apiGet(`/dashboard/cves/${encodeURIComponent(cveId)}/intel`, refresh ? { refresh: true } : undefined);
      setFacts(res);
      setAi(res.ai);
      setAiState(res.ai && !res.ai.unavailable ? 'ready' : 'unavailable');
      if (refresh && res.ai && !res.ai.unavailable) toast.success(`${cveId} analysis rewritten`);
    } catch (err) {
      setAiState('unavailable');
      if (refresh) toast.error(`Couldn't rewrite the analysis: ${describeError(err)}`);
    }
  }, [cveId]);

  const load = useCallback(async () => {
    setError(null);
    try {
      setFacts(await apiGet(`/dashboard/cves/${encodeURIComponent(cveId)}/intel`, { ai: false }));
      loadAi(false);
    } catch (err) {
      setError(err);
    }
  }, [cveId, loadAi]);

  useEffect(() => { load(); }, [load]);

  const huntCount = ai && !ai.unavailable ? (ai.hunting?.queries?.length || 0) : 0;
  useEffect(() => { if (onHuntCount) onHuntCount(aiState === 'loading' ? null : huntCount); }, [aiState, huntCount, onHuntCount]);


  if (error) return <ErrorState variant="inline" title={`Couldn't load ${cveId}`} error={error} onRetry={load} />;
  if (!facts) {
    return (
      <div className="vi-pending" role="status" aria-label={`Loading ${cveId}`}>
        <Skeleton style={{ height: 56 }} /><Skeleton style={{ height: 40 }} /><Skeleton style={{ height: 12, width: '70%' }} />
      </div>
    );
  }
  const view = ai && !ai.unavailable ? ai : null;
  const props = { d: facts, ai: view, aiState, retry: () => loadAi(false) };
  return (
    <div className="vi" data-testid={`vuln-brief-${cveId}`}>
      <Tabs value={tab} onValueChange={setTab}>
        <TabList label={`${cveId} brief sections`}>
          <Tab value="overview">Overview</Tab>
          <Tab value="remediation">Remediation</Tab>
          <Tab value="hunting" count={huntCount || undefined}>Threat hunting</Tab>
          <Tab value="sources">Sources</Tab>
        </TabList>
        <TabPanel value="overview"><Overview {...props} /></TabPanel>
        <TabPanel value="remediation"><Remediation {...props} /></TabPanel>
        <TabPanel value="hunting"><Hunting {...props} /></TabPanel>
        <TabPanel value="sources"><Sources d={facts} ai={view} busy={aiState === 'loading'} regenerate={() => loadAi(true)} /></TabPanel>
      </Tabs>
    </div>
  );
}
